import { ChevronDown, PencilRuler, Trash2 } from "lucide-react";
import { Fragment } from "react";
import { Button } from "@/shared/ui/Button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/shared/ui/DropDownMenu";
import type { LucideIcon } from "lucide-react";

export type ActionItem = {
  label: string
  onClick: () => void
  icon?: LucideIcon
  className?: string
  separatorAfter?: boolean
};

export type ActionMenuProps = {
  actions: ActionItem[]
  className?: string
  /** ADDITIONAL CLASSES FOR DROPDOWN CONTENT */
  contentClassName?: string
  disabled?: boolean
};

// ----- UTILITY FUNCTIONS ----- //
const isDeleteAction = (action: ActionItem): boolean => {
  return action.label.toLowerCase().includes("supprimer");
};

const separateActions = (actions: ActionItem[]) => {
  const deleteActions = actions.filter(isDeleteAction);
  const otherActions = actions.filter((action) => !isDeleteAction(action));
  return { deleteActions, otherActions };
};

export const ActionMenu = ({
  actions, className = "", contentClassName = "", disabled = false,
}: ActionMenuProps) => {
  // Separate delete actions from other actions
  const { deleteActions, otherActions } = separateActions(actions);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          className={`${className} group cursor-pointer flex items-center space-x-1 bg-gray-100`}
          onClick={(e) => e.stopPropagation()}
          disabled={disabled}
        >
          <PencilRuler />
          Actions
          <ChevronDown
            className="transition-transform duration-200 group-data-[state=open]:rotate-180"
            aria-hidden
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={`min-w-58 ${contentClassName}`}
        onClick={(e) => e.stopPropagation()}
      >
        {otherActions.map((action, index) => (
          <Fragment key={index}>
            <DropdownMenuItem
              onSelect={(e) => {
                e.stopPropagation();
                action.onClick();
              }}
              className={`whitespace-nowrap ${action.className ?? ""}`}
            >
              {action.icon && <action.icon size={16} />}
              {action.label}
            </DropdownMenuItem>
            {action.separatorAfter && <DropdownMenuSeparator />}
          </Fragment>
        ))}

        {deleteActions.length > 0 && otherActions.length > 0 && (
          <DropdownMenuSeparator />
        )}

        {deleteActions.map((action, index) => (
          <DropdownMenuItem
            key={`delete-${index}`}
            onSelect={(e) => {
              e.stopPropagation();
              action.onClick();
            }}
            className="text-destructive"
          >
            <Trash2 size={16} />
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
